<template>
  <section class="lg:max-w-[600px] lg:mx-auto lg:!px-0">
    <div
      v-show="isVerifyingPayment"
      class="w-screen h-screen top-0 left-0 fixed bg-body-primary flex flex-col justify-start items-center py-20 space-y-4 z-50"
    >
      <h1 class="text-xl font-heading">Verifying payment...</h1>
      <div class="w-full max-w-sm bg-gray-200 rounded-full h-2 overflow-hidden">
        <div class="bg-primary h-2 rounded-full w-1/4 animate-indeterminate-progress"></div>
      </div>
    </div>
    <StatusBar v-if="!isDesktopFlow" />
    <section :class="{ 'px-4': !isDesktopFlow }">
      <h1 class="text-2xl md:text-4xl font-bold font-heading mb-8">Checkout</h1>
      <p>{{ contact.first_name }} {{ contact.last_name }}</p>
      <p>{{ contact.email }}</p>
      <p>{{ contact.phone }}</p>
      <button
        id="contact-info-summary"
        class="text-button-guest-primary underline mt-2 inline-block"
        @click="router.go(-1)"
      >
        Edit
      </button>

      <ButtonGroupSelector
        v-if="isGiftCardRedemptionEnabled && !isPartialPaymentWithGiftCard"
        :model-value="paymentMethod"
        :options="paymentMethodOptions"
        :disabled="isPaying"
        class="mt-6 mb-2"
        id="payment-method-selector"
        @update:modelValue="
          (value: string) => handlePaymentMethodClick(value as 'card' | 'gift_card')
        "
      />
      <CheckoutStripeForm v-show="paymentMethod === 'card'" ref="stripeForm" class="mb-6" />
      <CheckoutGiftCard v-show="paymentMethod === 'gift_card'" ref="giftCardForm" class="mb-6" />
      <p
        v-if="isPartialPaymentWithGiftCard"
        id="partial-payment-with-gift-card-message"
        class="p-2 my-4 rounded border border-yellow-400 bg-yellow-100 text-yellow-800"
      >
        Your gift card does not cover the full amount. Please pay the remaining balance with a
        payment method above.
      </p>
      <p
        v-if="error.message"
        id="error-display"
        class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
      >
        {{ error.message }}
      </p>
      <div v-if="!isDesktopFlow" class="price-information">
        <CheckoutDetailTable class="mb-8" />
        <CheckoutPromoCode class="my-8" />
      </div>
      <div v-if="checkout_confirmation_text" class="mt-7">
        <CheckboxInput
          id="accepts-checkout-confirmation"
          v-model="acceptsCheckoutConfirmation"
          label="I agree to the following policy"
          guest
        />
        <div
          class="text-sm text-fg-g-primary mt-2 whitespace-pre-line"
          id="checkout-confirmation-text"
        >
          {{ checkout_confirmation_text }}
        </div>
      </div>
      <p v-show="checkoutText" class="checkout-text text-sm text-fg-g-primary mt-7">
        {{ checkoutText }}
      </p>
    </section>
    <div class="px-4 block lg:hidden">
      <GuestLegalLink />
    </div>
    <CheckoutBottomBar
      id="checkout-bottom-bar"
      @click:next="submitPayment"
      :isNextButtonDisabled="isBottomBarButtonDisabled"
      :nextButtonText="bottomBarButtonText"
      :isPaymentProcessing="isPaying || isPaymentProcessing"
    />
    <GuestLegalLink class="hidden lg:block" />
  </section>
</template>

<script setup lang="ts">
import type { PaymentIntentResult, Stripe } from '@stripe/stripe-js'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CheckboxInput from '@/common/components/molecules/CheckboxInput.vue'
import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import GuestLegalLink from '@/modules/guestUi/components/atoms/GuestLegalLink.vue'
import ButtonGroupSelector from '@/modules/guestUi/components/molecules/ButtonGroupSelector.vue'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import CheckoutDetailTable from '@/modules/guestUi/components/organisms/CheckoutDetailTable.vue'
import CheckoutGiftCard from '@/modules/guestUi/components/organisms/CheckoutGiftCard.vue'
import CheckoutPromoCode from '@/modules/guestUi/components/organisms/CheckoutPromoCode.vue'
import CheckoutStripeForm from '@/modules/guestUi/components/organisms/CheckoutStripeForm.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { useStripePaymentStatus } from '@/modules/guestUi/composables/useStripePaymentStatus'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, clearError, displayHandled, setError } from '@/utils/errors'

const router = useRouter()
const checkoutStore = useCheckoutStore()
const { isDesktopFlow, contact, client_secret, performance } = storeToRefs(checkoutStore)
const settingStore = useSettingStore()
const { checkout_confirmation_text, checkout_text } = storeToRefs(settingStore)

const checkoutText = computed<string>(() => {
  return performance.value?.checkout_text || checkout_text.value || ''
})

const stripeForm = useTemplateRef<InstanceType<typeof CheckoutStripeForm>>('stripeForm')
const giftCardForm = useTemplateRef<InstanceType<typeof CheckoutGiftCard>>('giftCardForm')
// Gift Card
const { isGiftCardRedemptionEnabled } = useFeatureFlags()
const paymentMethod = ref<'card' | 'gift_card'>('card')
const isPartialPaymentWithGiftCard = ref<boolean>(false)
const paymentMethodOptions = [
  { value: 'card', label: 'Pay with Card' },
  { value: 'gift_card', label: 'Pay with Gift Card' },
]
async function handlePaymentMethodClick(method: 'card' | 'gift_card'): Promise<void> {
  paymentMethod.value = method
  if (method === 'card') {
    await stripeForm.value?.mountStripeForm()
  } else {
    stripeForm.value?.unmountStripeForm()
  }
}
function handlePartialPayment() {
  handlePaymentMethodClick('card')
  isPartialPaymentWithGiftCard.value = true
}

// Stripe Payment Polling
const error = ref<ErrorData>(blankError())
const stripe = computed<Stripe | null>(() => stripeForm.value?.stripe ?? null)
const { isPaymentProcessing, isVerifyingPayment, verifyPayment, hasPaidExternally } =
  useStripePaymentStatus({
    error,
    stripe,
  })
onMounted(async function () {
  if (hasPaidExternally.value) {
    isVerifyingPayment.value = true
    await handlePaymentMethodClick('card')
    // Wait for the stripe form to be mounted,
    // so the `stripe` ref is available and has a valid value
    setTimeout(verifyPayment, 1000)
  } else {
    await handlePaymentMethodClick('card')
  }
})

// Paying logic
const route = useRoute()
const acceptsCheckoutConfirmation = ref<boolean>(false)
const isPaying = ref<boolean>(false)
async function submitPayment() {
  if (isBottomBarButtonDisabled.value) return
  isPaying.value = true
  clearError(error.value)
  try {
    let stripeResponse: PaymentIntentResult | undefined
    if (stripeForm.value) {
      const viteParam = route.query.vite ? '&vite=1' : ''
      const returnUrl = `${window.location.origin}/payment/?reservation_id=${
        checkoutStore.reservation!.id
      }&reservation_key=${checkoutStore.reservation_key}${viteParam}`
      await stripeForm.value.elements?.submit()
      stripeResponse = await stripeForm.value.stripe?.confirmPayment({
        elements: stripeForm.value.elements!,
        clientSecret: client_secret.value!,
        redirect: 'if_required',
        confirmParams: { return_url: returnUrl },
      })
      if (stripeResponse?.error) {
        setError(error.value, stripeResponse.error)
        return
      }
    }
    if (paymentMethod.value === 'card') {
      await checkoutStore.checkout(stripeResponse)
    } else if (paymentMethod.value === 'gift_card') {
      const paid = await checkoutStore.checkoutWithGiftCard(
        giftCardForm.value!.giftCardNumber,
        giftCardForm.value!.giftCardPin,
      )
      if (paid === false) handlePartialPayment()
    }
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isPaying.value = false
  }
}

// Bottom Bar
const bottomBarButtonText = computed<string>(() => {
  return isPaying.value ? 'Processing...' : 'Purchase'
})
const isCheckboxValid = computed<boolean>(
  () => !checkout_confirmation_text.value || acceptsCheckoutConfirmation.value,
)
const isBottomBarButtonDisabled = computed<boolean>(() => {
  if (isPaying.value || !isCheckboxValid.value || isPaymentProcessing.value) {
    return true
  }

  switch (paymentMethod.value) {
    case 'gift_card':
      return !giftCardForm.value?.isFormValid
    case 'card':
      return !stripeForm.value?.isStripeFormComplete
    default:
      return false
  }
})
</script>
